import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { PaymentMethodsDTO, ResidentDTO, FeesDTO, TransactionDTO, BankAccountDTO, BankCardDTO, PaymentsConfigDto, ScheduledAutoPayDto, BalanceDetailsDTO, PaidByEnum } from '@inosio/payments-sdk';

type ResidentPaymentsState = {
  BalanceDetails: BalanceDetailsDTO;
  isFetching: boolean;
  isFetchingPaymentFeesByResident: boolean;
  paymentFeesError: string;
  isBankEditedSuccess: boolean;
  isBankAddedSuccess: boolean;
  isCardSuccess: boolean;
  isBankDeleted: boolean;
  isCardDeleted: boolean;
  isRecurringSuccess: boolean;
  recurringPaymentError: string | null;
  isReplaceAutoPaySuccess: boolean;
  replaceAutoPayError: string | null;
  isError: boolean;
  errorMessage: string | null;
  cardErrorMessage: string | null;
  bankErrorMessage: string | null;
  PaymentMethods: PaymentMethodsDTO;
  TransactionHistory: TransactionDTO[];
  ResidentDetails: ResidentDTO;
  PaymentFeesByResident: FeesDTO[];
  RawPaymentFeesByResident: FeesDTO[];
  PaymentMadeDetails: TransactionDTO;
  BankCardTypeBIN: any;
  CreateBankAccResponse: BankAccountDTO;
  CreateBankCardResponse: BankCardDTO;
  UpdateBankAccountResponse: object;
  UpdateBankCardResponse: BankCardDTO;
  DeleteBankAccountResponse: object;
  DeleteBankCardResponse: object;
  paymentConfigs: PaymentsConfigDto;
  CreateRecurringPaymentResponse: ScheduledAutoPayDto;
  AutoPays: ScheduledAutoPayDto[];
  ReplaceAutoPayResponse: ScheduledAutoPayDto;
  SkipAutoPayResponse: ScheduledAutoPayDto;
  isSkipAutoPaySuccess: boolean;
  skipAutoPayError: string | null;
  isCancelAutoPaySuccess: boolean;
  cancelAutoPayError: string | null;
  isCancelAutoPayFetching: boolean;
  isCancelAutoPayError: boolean;
  isMakeOtpError: boolean;
  makeOneTimePaymentError: string | null;
  isMakeOtpSuccess: boolean;
  isFetchingPaymentHistoryFailed: boolean;
  isFetchingPaymentHistorySuccess: boolean;
  isPaymentMethodsFailed: boolean;
  isAutoPaysFailed: boolean;
  isBalanceFailed: boolean;
  getAutoPaysError: string | null;
  isFetchingAutoPays: boolean;
  isFetchingBalance: boolean;
  isFetchingPaymentMethods: boolean;
  isFetchingPaymentHistory: boolean;
  isResidentDetailsFailed: boolean;
  isPaymentFeesByResidentFailed: boolean;
  isPaymentFeesByResidentSuccess: boolean;
  isBankCardTypeFailed: boolean;
  isGetPaymentConfigsFailed: boolean;
  isCancelOTPSuccess: boolean;
  isCancelOTPError: boolean;
  isCancelOTPFetching: boolean;
  cancelOTPError: string | null;
  isRefundOTPFetching: boolean;
  isRefundOTPSuccess: boolean;
  isRefundOTPError: boolean;
  refundOTPError: string | null;
  isCancelStatusFetching: boolean;
  isCancelStatusSuccess: boolean;
  isCancelStatusError: boolean;
  cancelStatusError: string | null;
  cancelStatusResponse: boolean;
  isFetchingCreateBankAccount: boolean;
  isFetchingCreateBankCard: boolean;
  RawPaymentFeeByResident: FeesDTO[];
};

const getInitialState = () => {
  return {
    isFetching: false,
    isFetchingPaymentFeesByResident: false,
    isPaymentFeesByResidentSuccess: false,
    paymentFeesError: '',
    isFetchingAutoPays: false,
    isBankEditedSuccess: false,
    isBankAddedSuccess: false,
    isCardSuccess: false,
    isBankDeleted: false,
    isCardDeleted: false,
    isError: false,
    cardErrorMessage: '',
    bankErrorMessage: '',
    recurringPaymentError: '',
    isRecurringSuccess: false,
    isReplaceAutoPaySuccess: false,
    replaceAutoPayError: '',
    isSkipAutoPaySuccess: false,
    skipAutoPayError: '',
    isCancelAutoPaySuccess: false,
    cancelAutoPayError: '',
    isCancelAutoPayFetching: false,
    isCancelAutoPayError: false,
    isMakeOtpError: false,
    makeOneTimePaymentError: '',
    isMakeOtpSuccess: false,
    isFetchingPaymentHistoryFailed: false,
    isFetchingPaymentHistorySuccess: false,
    isPaymentMethodsFailed: false,
    isAutoPaysFailed: false,
    isBalanceFailed: false,
    getAutoPaysError: '',
    isFetchingBalance: true,
    isFetchingPaymentMethods: false,
    isFetchingPaymentHistory: true,
    isResidentDetailsFailed: false,
    isPaymentFeesByResidentFailed: false,
    isBankCardTypeFailed: false,
    isGetPaymentConfigsFailed: false,
    isCancelOTPSuccess: false,
    isCancelOTPError: false,
    isCancelOTPFetching: false,
    cancelOTPError: '',
    isRefundOTPFetching: false,
    isRefundOTPSuccess: false,
    isRefundOTPError: false,
    refundOTPError: '',
    isCancelStatusFetching: false,
    isCancelStatusSuccess: false,
    isCancelStatusError: false,
    cancelStatusError: '',
    cancelStatusResponse: false,
    isFetchingCreateBankAccount: false,
    isFetchingCreateBankCard: false,
    RawPaymentFeeByResident: [] as FeesDTO[],
    TransactionHistory: [] as TransactionDTO[],

    //* missing propertiers that are not initialized but should be. I was hesitent to add them because of the possible impact on downstream code that was relying on them being undefined to begin with.
    // AutoPays: [],
    // BalanceDetails: {} as BalanceDetailsDTO,//todo: confirm
    // BankCardTypeBIN: null, //todo: confirm
    // CreateBankAccResponse: {} as BankAccountDTO,
    // CreateBankCardResponse: {} as BankCardDTO,
    // CreateRecurringPaymentResponse: {} as ScheduledAutoPayDto,
    // DeleteBankAccountResponse: {},
    // DeleteBankCardResponse: {},
    // errorMessage: null,
    // paymentConfigs: {} as PaymentsConfigDto,
    // PaymentFeesByResident: [],
    // PaymentMadeDetails: {} as TransactionDTO,
    // PaymentMethods: {} as PaymentMethodsDTO,
    // RawPaymentFeesByResident: [],
    // ReplaceAutoPayResponse: {} as ScheduledAutoPayDto,
    // ResidentDetails: {} as ResidentDTO,
    // SkipAutoPayResponse: {} as ScheduledAutoPayDto,
    // UpdateBankAccountResponse: {},
    // UpdateBankCardResponse: {} as BankCardDTO
  } as ResidentPaymentsState;
};

const residentPaymentsSlice = createSlice({
  name: 'residentpayments',
  initialState: getInitialState(),
  reducers: {
    getResidentDetails(state) {},
    setResidentDetails(state, action) {
      const residentDetailsData = { ResidentDetails: action.payload, isResidentDetailsFailed: false };
      return { ...state, ...residentDetailsData };
    },
    getResidentDetailsFailed(state, action) {
      state.isResidentDetailsFailed = true;
    },
    getBalanceDetails() {},
    setBalanceDetails(state, action) {
      const balanceDetailsData = { BalanceDetails: action.payload, isBalanceFailed: false, isFetchingBalance: false };
      return { ...state, ...balanceDetailsData };
    },
    getBalanceDetailsFailed(state, action) {
      state.isBalanceFailed = true;
      state.isFetchingBalance = false;
    },
    getTransactionHistory(state) {
      state.isFetchingPaymentHistory = true;
      state.isFetchingPaymentHistorySuccess = false;
      state.isFetchingPaymentHistoryFailed = false;
    },
    setTransactionHistory(state, action) {
      const transactionHistoryData = {
        TransactionHistory: action.payload,
        isFetchingPaymentHistorySuccess: true,
        isFetchingPaymentHistoryFailed: false,
        isFetchingPaymentHistory: false,
      };
      return { ...state, ...transactionHistoryData };
    },
    getTransactionHistoryFailed(state, action) {
      state.isFetchingPaymentHistoryFailed = true;
      state.isFetchingPaymentHistorySuccess = false;
      state.isFetchingPaymentHistory = false;
    },
    getPaymentMethods(state) {
      state.isFetchingPaymentMethods = true;
    },
    setPaymentMethods(state, action) {
      const paymentMethodsData = {
        PaymentMethods: action.payload,
        isPaymentMethodsFailed: false,
        isFetchingPaymentMethods: false,
      };
      return { ...state, ...paymentMethodsData };
    },
    getPaymentMethodsFailed(state, action) {
      state.isPaymentMethodsFailed = true;
      state.isFetchingPaymentMethods = false;
    },
    getPaymentFeesByResident(state) {
      state.isPaymentFeesByResidentFailed = false;
      state.isFetchingPaymentFeesByResident = true;
      state.isPaymentFeesByResidentFailed = false;
      state.isPaymentFeesByResidentSuccess = false;
      state.paymentFeesError = '';
    },
    setPaymentFeesByResident(state, action) {
      const rawFees = action.payload as FeesDTO[];
      const feesRedacted = rawFees.map((rf) => {
        return rf.PaidBy === PaidByEnum.Payer ? rf : { ...rf, FeeAmount: 0 };
      }); //if res, keep as is, otherwise if PM make 0
      const paymentFeesByResident = {
        PaymentFeesByResident: feesRedacted,
        isPaymentFeesByResidentFailed: false,
        isFetchingPaymentFeesByResident: false,
        RawPaymentFeeByResident: rawFees,
        isPaymentFeesByResidentSuccess: true,
      };
      return { ...state, ...paymentFeesByResident, RawPaymentFeesByResident: rawFees };
    },
    getPaymentMethodsByResidentFailed(state, action) {
      state.isPaymentFeesByResidentFailed = true;
      state.isFetchingPaymentFeesByResident = false;
      state.paymentFeesError = action.payload.message;
    },
    makeOneTimePayment(state, action) {
      state.isFetching = true;
      state.isMakeOtpSuccess = false;
      state.isMakeOtpError = false;
      state.makeOneTimePaymentError = null;
    },
    setOneTimePaymentResult(state, action) {
      const paymentResponse = { PaymentMadeDetails: action.payload, isFetching: false, isMakeOtpSuccess: true };
      return { ...state, ...paymentResponse };
    },
    makeOTPfailed(state, action) {
      state.isFetching = false;
      state.isMakeOtpError = true;
      state.isMakeOtpSuccess = false;
      state.makeOneTimePaymentError = action.payload.message;
    },
    getOneTimePaymentResult(state, action) {},
    setBankCardType(state, action) {
      const bankCardType = { BankCardTypeBIN: action.payload, isBankCardTypeFailed: false };
      return { ...state, ...bankCardType };
    },
    getBankCardType(state, action) {},
    getBankCardTypeFailed(state, action) {
      state.isBankCardTypeFailed = true;
      state.isError = true;
      state.cardErrorMessage = action.payload.message;
    },
    clearCardType(state, action) {
      state.BankCardTypeBIN = {
        CardType: '',
        CardNetwork: '',
        CardIssuer: '',
      };
    },
    setCreateBankAccResponse(state, action) {
      const createBankAccRes = { CreateBankAccResponse: action.payload, isBankAddedSuccess: true, isFetching: false };
      return { ...state, ...createBankAccRes };
    },
    getCreateBankAccount(state, action) {
      state.isFetchingCreateBankAccount = true;
      state.isBankAddedSuccess = false;
    },
    clearBankAndCardStates(state, action) {
      state.isBankEditedSuccess = false;
      state.isCardSuccess = false;
      state.isBankAddedSuccess = false;
      state.isBankDeleted = false;
      state.isCardDeleted = false;
      state.cardErrorMessage = '';
      state.bankErrorMessage = '';
      state.isError = false;
    },
    clearPayModalReduxStates(state, action) {
      state.isFetching = false;
      state.isMakeOtpSuccess = false;
      state.isMakeOtpError = false;
      state.makeOneTimePaymentError = null;
      state.isCancelOTPFetching = false;
      state.isCancelOTPSuccess = false;
      state.isCancelOTPError = false;
      state.cancelOTPError = '';
      state.CreateBankAccResponse = {} as BankAccountDTO;
      state.CreateBankCardResponse = {} as BankCardDTO;
      state.PaymentMadeDetails = {} as TransactionDTO;
      state.isError = false;
      state.isRecurringSuccess = false;
      state.recurringPaymentError = '';
      state.isReplaceAutoPaySuccess = false;
      state.replaceAutoPayError = '';
    },
    createBankAccountFailed: (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.bankErrorMessage = action.payload.message;
    },
    setCreateBankCardResponse(state, action) {
      const createBankCardRes = { CreateBankCardResponse: action.payload, isCardSuccess: true, isFetching: false };
      return { ...state, ...createBankCardRes };
    },
    getCreateBankCard(state, action) {
      state.isFetchingCreateBankCard = true;
      state.isCardSuccess = false;
    },
    createCreateBankCardFailed: (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.cardErrorMessage = action.payload.message;
    },
    setUpdateBankAccResponse(state, action) {
      const updateBankAccRes = { UpdateBankAccountResponse: action.payload, isBankEditedSuccess: true, isFetching: false };
      return { ...state, ...updateBankAccRes };
    },
    getUpdateBankAccount(state, action) {
      state.isFetching = true;
      state.isBankEditedSuccess = false;
    },
    updateBankAccountFailed: (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.isBankEditedSuccess = false;
      state.bankErrorMessage = action.payload.message;
    },
    setDeleteBankAccResponse(state, action) {
      const deleteBankAccRes = { DeleteBankAccountResponse: action.payload, isBankDeleted: true, isFetching: false, isError: false, bankErrorMessage: null };
      return { ...state, ...deleteBankAccRes };
    },
    getDeleteBankAccount(state, action) {
      state.isFetching = true;
      state.isBankDeleted = false;
      state.isError = false;
      state.bankErrorMessage = null;
    },
    deleteBankAccountFailed(state, action) {
      state.isFetching = false;
      state.isBankDeleted = false;
      state.isError = true;
      state.bankErrorMessage = action.payload.message;
    },
    setUpdateBankCardResponse(state, action) {
      const updateBankCardRes = { UpdateBankCardResponse: action.payload, isCardSuccess: true, isFetching: false };
      return { ...state, ...updateBankCardRes };
    },
    getUpdateBankCard(state, action) {
      state.isFetching = true;
      state.isCardSuccess = false;
    },
    updateBankCardFailed(state, action) {
      state.isFetching = false;
      state.isCardSuccess = false;
      state.isError = true;
      state.cardErrorMessage = action.payload.message;
    },
    setDeleteBankCardResponse(state, action) {
      const deleteBankCardRes = { DeleteBankCardResponse: action.payload, isCardDeleted: true, isFetching: false, isError: false, cardErrorMessage: null };
      return { ...state, ...deleteBankCardRes };
    },
    getDeleteBankCard(state, action) {
      state.isFetching = true;
      state.isCardDeleted = false;
      state.isError = false;
      state.cardErrorMessage = null;
    },
    deleteBankCardFailed(state, action) {
      state.isFetching = false;
      state.isCardDeleted = false;
      state.isError = true;
      state.cardErrorMessage = action.payload.message;
    },
    getPaymentConfigs() {},
    setgetPaymentConfigs(state, action) {
      const paymentConfigData = { paymentConfigs: action.payload, isGetPaymentConfigsFailed: false };
      return { ...state, ...paymentConfigData };
    },
    getPaymentConfigsFailed(state, action) {
      state.isGetPaymentConfigsFailed = true;
    },
    setCreateRecurringPaymentResponse(state, action) {
      const createRecurringPaymentRes = {
        CreateRecurringPaymentResponse: action.payload,
        isRecurringSuccess: true,
        isFetching: false,
      };
      return { ...state, ...createRecurringPaymentRes };
    },
    getCreateRecurringPayment(state, action) {
      state.isFetching = true;
      state.isError = false;
      state.isRecurringSuccess = false;
      state.recurringPaymentError = '';
    },
    getCreateRecurringPaymentFailed: (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.isRecurringSuccess = false;
      state.recurringPaymentError = action.payload.message;
    },
    clearRecurringSuccess(state, action) {
      state.isRecurringSuccess = false;
    },
    getAutoPays(state) {
      state.isFetchingAutoPays = true;
      state.isError = false;
      state.getAutoPaysError = '';
      state.isAutoPaysFailed = false;
    },
    setGetAutoPays(state, action) {
      const autopays = {
        AutoPays: action.payload,
        isFetchingAutoPays: false,
        isAutoPaysFailed: false,
      };
      return { ...state, ...autopays };
    },

    getAutoPaysFailed(state, action) {
      state.isAutoPaysFailed = true;
      state.isFetchingAutoPays = false;
      state.isError = true;
      state.getAutoPaysError = action.payload;
    },
    clearGetAutoPaysState(state) {
      state.isAutoPaysFailed = false;
      state.isFetchingAutoPays = false;
      state.isError = false;
      state.getAutoPaysError = '';
    },
    setReplaceAutoPayResponse(state, action) {
      const replaceAutoPayRes = {
        ReplaceAutoPayResponse: action.payload,
        isReplaceAutoPaySuccess: true,
        isFetching: false,
      };
      return { ...state, ...replaceAutoPayRes };
    },
    getReplaceAutoPay(state, action) {
      state.isFetching = true;
      state.isError = false;
      state.isReplaceAutoPaySuccess = false;
      state.replaceAutoPayError = '';
    },
    getReplaceAutoPayFailed: (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.isReplaceAutoPaySuccess = false;
      state.replaceAutoPayError = action.payload.message;
    },
    clearReplaceAutoPaySuccess(state, action) {
      state.isReplaceAutoPaySuccess = false;
    },

    setSkipAutoPayResponse(state, action) {
      const skipAutoPayRes = {
        SkipAutoPayResponse: action.payload,
        isSkipAutoPaySuccess: true,
        isFetching: false,
      };
      return { ...state, ...skipAutoPayRes };
    },

    getSkipAutoPay(state, action) {
      state.isFetching = true;
      state.isError = false;
      state.isSkipAutoPaySuccess = false;
      state.skipAutoPayError = '';
    },
    setSkipAutoPayFailed: (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.isSkipAutoPaySuccess = false;
      state.skipAutoPayError = action.payload.message;
    },
    getCancelAutoPay(state, action) {
      state.isCancelAutoPayFetching = true;
      state.isCancelAutoPayError = false;
      state.isCancelAutoPaySuccess = false;
      state.cancelAutoPayError = '';
    },
    setCancelAutoPayFailed: (state, action) => {
      state.isCancelAutoPayFetching = false;
      state.isCancelAutoPayError = true;
      state.isCancelAutoPaySuccess = false;
      state.cancelAutoPayError = action.payload.message;
    },

    setCancelAutoPayResponse(state, action) {
      const cancelAutoPayRes = {
        isCancelAutoPaySuccess: true,
        isCancelAutoPayFetching: false,
      };
      return { ...state, ...cancelAutoPayRes };
    },
    clearCancelAutoPaySuccess(state, action) {
      state.isCancelAutoPaySuccess = false;
    },

    // track OTP cancelation
    getCancelOTP(state, action) {
      state.isCancelOTPFetching = true;
      state.isCancelOTPSuccess = false;
      state.isCancelOTPError = false;
      state.cancelOTPError = '';
    },
    setCancelOTPResponse(state, action) {
      return { ...state, isCancelOTPSuccess: true, isCancelOTPFetching: false };
    },
    setCancelOTPFailed(state, action) {
      state.isCancelOTPFetching = false;
      state.isCancelOTPSuccess = false;
      state.isCancelOTPError = true;
      state.cancelOTPError = action.payload.message;
    },
    getRefundOTP(state, action) {
      state.isRefundOTPFetching = true;
      state.isRefundOTPSuccess = false;
      state.isRefundOTPError = false;
      state.refundOTPError = '';
    },
    setRefundOTPResponse(state, action) {
      return { ...state, isRefundOTPSuccess: true, isRefundOTPFetching: false };
    },
    setRefundOTPError(state, action) {
      state.isRefundOTPFetching = false;
      state.isRefundOTPSuccess = false;
      state.isRefundOTPError = true;
      state.refundOTPError = action.payload.message;
    },
    getCancelStatus(state, action) {
      state.isCancelStatusFetching = true;
      state.isCancelStatusSuccess = false;
      state.isCancelStatusError = false;
      state.cancelStatusResponse = false;
      state.cancelStatusError = '';
    },
    setCancelSatusResponse(state, action) {
      state.isCancelStatusFetching = false;
      state.isCancelStatusSuccess = true;
      state.isCancelStatusError = false;
      state.cancelStatusResponse = action.payload.canCancel;
      state.cancelStatusError = '';
    },
    setCancelStatusError(state, action) {
      state.isCancelStatusFetching = false;
      state.isCancelStatusSuccess = false;
      state.isCancelStatusError = true;
      state.cancelStatusResponse = false;
      state.cancelStatusError = action.payload.message;
    },
    // getCompanyDetails(state) {
    //   state.isFetchingCompanyDetails = true;
    //   state.isFetchingCompanyDetailsSuccess = false;
    //   state.CompanyDetails = {} as CompanyBasicConfigsDto;
    // },
    // setCompanyDetails(state, action) {
    //   state.isFetchingCompanyDetails = false;
    //   state.isFetchingCompanyDetailsSuccess = true;
    //   state.CompanyDetails = action.payload;
    // },
    // setCompanyDetailsError(state, action) {
    //   state.isFetchingCompanyDetails = false;
    //   state.isFetchingCompanyDetailsSuccess = false;
    //   state.CompanyDetailsError = action.payload;
    // },
    // getPropertyGroupDetails(state) {
    //   state.isFetchingPropertyGroupDetails = true;
    //   state.isFetchingPropertyGroupDetailsSuccess = false;
    //   state.PropertyGroupDetails = {} as PropertyGroupDetailDto;
    // },
    // setPropertyGroupDetails(state, action) {
    //   state.isFetchingPropertyGroupDetails = false;
    //   state.isFetchingPropertyGroupDetailsSuccess = true;
    //   state.PropertyGroupDetails = action.payload;
    // },
    // setPropertyGroupDetailsError(state, action) {
    //   state.isFetchingPropertyGroupDetails = false;
    //   state.isFetchingPropertyGroupDetailsSuccess = false;
    //   state.PropertyGroupDetailsError = action.payload;
    // }
  },
});

export const {
  getBalanceDetails,
  setBalanceDetails,
  getTransactionHistory,
  setTransactionHistory,
  getPaymentMethods,
  setPaymentMethods,
  getResidentDetails,
  setResidentDetails,
  getPaymentFeesByResident,
  setPaymentFeesByResident,
  setOneTimePaymentResult,
  makeOneTimePayment,
  setBankCardType,
  clearCardType,
  getBankCardType,
  setCreateBankAccResponse,
  getCreateBankAccount,
  setCreateBankCardResponse,
  getCreateBankCard,
  createBankAccountFailed,
  createCreateBankCardFailed,
  setUpdateBankAccResponse,
  getUpdateBankAccount,
  getTransactionHistoryFailed,
  setUpdateBankCardResponse,
  getUpdateBankCard,
  setDeleteBankAccResponse,
  getDeleteBankAccount,
  setDeleteBankCardResponse,
  getDeleteBankCard,
  updateBankAccountFailed,
  updateBankCardFailed,
  deleteBankAccountFailed,
  deleteBankCardFailed,
  clearBankAndCardStates,
  getPaymentConfigs,
  setgetPaymentConfigs,
  setCreateRecurringPaymentResponse,
  getCreateRecurringPayment,
  getCreateRecurringPaymentFailed,
  clearRecurringSuccess,
  getAutoPays,
  setGetAutoPays,
  setReplaceAutoPayResponse,
  getReplaceAutoPay,
  getReplaceAutoPayFailed,
  clearReplaceAutoPaySuccess,
  setSkipAutoPayFailed,
  setSkipAutoPayResponse,
  getSkipAutoPay,
  getCancelAutoPay,
  setCancelAutoPayFailed,
  setCancelAutoPayResponse,
  clearCancelAutoPaySuccess,
  makeOTPfailed,
  clearPayModalReduxStates,
  getBalanceDetailsFailed,
  getPaymentMethodsFailed,
  getAutoPaysFailed,
  clearGetAutoPaysState,
  getResidentDetailsFailed,
  getPaymentMethodsByResidentFailed,
  getBankCardTypeFailed,
  getPaymentConfigsFailed,
  getCancelOTP,
  setCancelOTPResponse,
  setCancelOTPFailed,
  getRefundOTP,
  setRefundOTPError,
  setRefundOTPResponse,
  getCancelStatus,
  setCancelSatusResponse,
  setCancelStatusError,
} = residentPaymentsSlice.actions;

export default residentPaymentsSlice.reducer;
export const selectResidentDetails = (state: RootState) => state.pmtsModuleState.ResidentDetails;
export const selectBalanceDetails = (state: RootState) => state.pmtsModuleState.BalanceDetails;
export const selectPaymentMethods = (state: RootState) => state.pmtsModuleState.PaymentMethods;
export const selectTransactionHistory = (state: RootState) => state.pmtsModuleState.TransactionHistory;
export const selectPaymentFeesByResident = (state: RootState) => state.pmtsModuleState.PaymentFeesByResident;
export const selectMakeOneTimePayment = (state: RootState) => state.pmtsModuleState.PaymentMadeDetails;
export const getOneTimePaymentResult = (state: RootState) => state.pmtsModuleState.PaymentMadeDetails;
export const selectBankCardTypeDetails = (state: RootState) => state.pmtsModuleState.BankCardTypeBIN;
export const selectBankAccResponseDetails = (state: RootState) => state.pmtsModuleState.CreateBankAccResponse;
export const selectBankCardResponseDetails = (state: RootState) => state.pmtsModuleState.CreateBankCardResponse;
export const selectUpdateBankAccRespDetails = (state: RootState) => state.pmtsModuleState.UpdateBankAccountResponse;
export const selectUpdateBankCardRespDetails = (state: RootState) => state.pmtsModuleState.UpdateBankCardResponse;
export const selectDeleteBankAccountRespDetails = (state: RootState) => state.pmtsModuleState.DeleteBankAccountResponse;
export const selectDeleteBankCardRespDetails = (state: RootState) => state.pmtsModuleState.DeleteBankCardResponse;
export const selectPaymentConfigs = (state: RootState) => state.pmtsModuleState.paymentConfigs;
export const selectCreateRecurringPaymentRes = (state: RootState) => state.pmtsModuleState.CreateRecurringPaymentResponse;
export const selectReplaceAutoPayResponse = (state: RootState) => state.pmtsModuleState.ReplaceAutoPayResponse;
export const selectAutoPays = (state: RootState) => state.pmtsModuleState.AutoPays;
export const selectSkipAutopayResponse = (state: RootState) => state.pmtsModuleState.SkipAutoPayResponse;
export const selectPaymentState = (state: RootState) => state.pmtsModuleState;
